import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * "Create something" that arrives as a link.
 *
 * The home screen's **Criar** menu opens the create affordance that already lives on another
 * screen — the lead dialog, the funnel's composer, the task field — rather than growing a second
 * copy of each form. The intent therefore has to survive a navigation, and `?criar=1` carries it.
 *
 * It is consumed on arrival. Left in the address bar it would reopen the form on every reload and
 * every press of the back button, which is how a link like this becomes a screen somebody cannot
 * get out of.
 *
 * `ready` is for a screen whose create needs something loaded first — the funnel needs its stages
 * before it can open a composer in one of them.
 */
export function useCreateIntent(onCreate: () => void, ready = true): void {
  const [params, setParams] = useSearchParams();
  const fired = useRef(false);
  const wanted = params.get('criar') === '1';

  useEffect(() => {
    if (!wanted || !ready || fired.current) return;

    fired.current = true;
    onCreate();

    const next = new URLSearchParams(params);
    next.delete('criar');
    setParams(next, { replace: true });
  }, [wanted, ready, onCreate, params, setParams]);
}
